import React from 'react';
import layout from '../marketing/marketing.module.css';
import styles from './HowTo.module.css';
import MarketingHead from '../marketing/components/MarketingHead';
import SiteHeader from '../marketing/components/SiteHeader';
import SiteFooter from '../marketing/components/SiteFooter';
import CtaBand from '../marketing/components/CtaBand';
import Button from '../marketing/components/Button';
import Eyebrow from '../marketing/components/Eyebrow';
import FeatureCard from '../marketing/components/FeatureCard';
import StepRow from '../marketing/components/StepRow';
import {
    UploadDiagramIllustrationDesktop,
    UploadDiagramIllustrationMobile,
    NamePageIllustrationDesktop,
    NamePageIllustrationMobile,
    TaskOutIllustrationDesktop,
    TaskOutIllustrationMobile,
} from '../marketing/illustrations/HowToIllustrations';
import {
    NotesIllustrationDesktop,
    NotesIllustrationMobile,
    TicketCreationIllustrationDesktop,
    TicketCreationIllustrationMobile,
} from '../marketing/illustrations/MoreIllustrations';
import {CREATE_PAGE_ROUTE, VIDEOS_ROUTE, HELP_ROUTE} from '../marketing/routes';

const STEPS_NAV = [
    {id: 'step1', number: '01', label: 'Select your background'},
    {id: 'step2', number: '02', label: 'Name your page'},
    {id: 'step3', number: '03', label: 'Task out'},
];

const DtMb = ({desktop, mobile}) => (
    <>
        <div className={layout.dtOnly}>{desktop}</div>
        <div className={layout.mbOnly}>{mobile}</div>
    </>
);

const HowTo = () => (
    <div className={layout.root}>
        <MarketingHead
            title="How to — Ovalview"
            description="A quick start guide to Ovalview, and to making your team's work visible."
        />
        <SiteHeader/>

        <main>
            <section className={styles.hero}>
                <Eyebrow>How to &middot; Quick start</Eyebrow>
                <h1 className={styles.h1}>Three steps to a page your team can read.</h1>
                <p className={styles.lead}>A quick start guide to Ovalview, and to making your team&apos;s work
                    visible.</p>
                <Button to={CREATE_PAGE_ROUTE} variant="primary" size="hero" icon="arrow" className={styles.ctaButton}>
                    I know already, create a page
                </Button>

                <nav aria-label="Steps" className={styles.stepsNav}>
                    {STEPS_NAV.map(({id, number, label}) => (
                        <a key={id} href={`#${id}`} className={styles.stepsNavLink}>
                            <span className={styles.stepsNavNumber}>{number}</span>
                            {label}
                        </a>
                    ))}
                </nav>
            </section>

            <section className={`${layout.container} ${styles.steps}`}>
                <StepRow
                    id="step1"
                    number="01"
                    title="Select your background"
                    lead="This is the design you'll use as the basis for tasking."
                    body="Any diagram that expresses your design works: a sequence diagram, an architecture diagram, a sketch captured from the whiteboard. If it shows the feature in a way that can be broken down, it's the right diagram."
                    illustration={
                        <DtMb desktop={<UploadDiagramIllustrationDesktop/>} mobile={<UploadDiagramIllustrationMobile/>}/>
                    }
                />
                <StepRow
                    id="step2"
                    number="02"
                    title="Name your page"
                    lead="This is the title, the name you'll use to find your page again."
                    body={'A descriptive title makes pages easier to find. Calling it “Page 1” will be less fun once your library has grown.'}
                    reverse
                    illustration={
                        <DtMb desktop={<NamePageIllustrationDesktop/>} mobile={<NamePageIllustrationMobile/>}/>
                    }
                />
                <StepRow
                    id="step3"
                    number="03"
                    title="Task out"
                    lead="Draw an oval around each task and give it a brief description."
                    body="That's all you need. The diagram provides the context."
                    illustration={
                        <DtMb desktop={<TaskOutIllustrationDesktop/>} mobile={<TaskOutIllustrationMobile/>}/>
                    }
                />
            </section>

            <section className={`${layout.section} ${layout.container}`}>
                <div className={styles.moreHeader}>
                    <div className={styles.moreHeading}>
                        <Eyebrow>And there&apos;s more</Eyebrow>
                        <h2 className={styles.h2}>Jira, organisations, folders and collaboration.</h2>
                    </div>
                    <Button to={HELP_ROUTE} variant="accentOutline" size="section" icon="arrow"
                            className={styles.tellMeHow}>
                        Tell me how
                    </Button>
                </div>

                <div className={styles.moreGrid}>
                    <FeatureCard
                        title="Notes"
                        illustration={<DtMb desktop={<NotesIllustrationDesktop/>} mobile={<NotesIllustrationMobile/>}/>}
                    >
                        <p>Need to add notes? Stick a note to your diagram.</p>
                    </FeatureCard>
                    <FeatureCard
                        dark
                        title="Ticket creation"
                        illustration={
                            <DtMb desktop={<TicketCreationIllustrationDesktop/>}
                                  mobile={<TicketCreationIllustrationMobile/>}/>
                        }
                    >
                        <p>With the Jira integration, tickets are created when you save the page: one story for the
                            page, and one task for each oval, titled with the text inside it.</p>
                    </FeatureCard>
                </div>
            </section>

            <div className={layout.section}>
                <CtaBand heading="Ready to map your first page?">
                    <Button to={CREATE_PAGE_ROUTE} variant="onDark" size="hero" icon="arrow">
                        Create a page
                    </Button>
                    <Button to={VIDEOS_ROUTE} variant="outlineOnDark" size="hero">
                        Watch video guides
                    </Button>
                </CtaBand>
                <SiteFooter/>
            </div>
        </main>
    </div>
);

export default HowTo;
